import { useParams, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { usePublicDiagnosticReport } from "../hooks/usePublicDiagnosticReport";
import AvatarIndicator from "../components/AvatarIndicator";
import TalentDiagnosticsSummary from "../components/TalentDiagnosticsSummary";
import Spinner from "../components/Spinner";
import { CategoryScore, DiagnosticsResponse } from "../types/diagnostics";
import { Search, AlertCircle, Lightbulb, Megaphone, Shield, Sparkles, Gauge, Home, Zap, Target } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { normalizeTalentDiagnosticsAnalysisResponse } from "../utils/talentDiagnosticsPublic";
import strivreLogo from '../assets/images/strivre-logo.svg';

export default function PublicDiagnosticReportPage() {
  const { token: reportToken } = useParams<{ token: string }>();
  // const location = useLocation();
  // const queryParams = new URLSearchParams(location.search);
  

  const { data: report, isLoading, isError, error } = usePublicDiagnosticReport(reportToken);
  const { token: authToken } = useAuth();
  const isAuthenticated = !!authToken;
  const navigate = useNavigate();
  const diagnosticType = report?.diagnosticType?.toLowerCase() || "website";
  const isTalentReport = diagnosticType === "talent";
  const websiteReport = !isTalentReport && report ? (report.report as DiagnosticsResponse) : null;

  const handleBookingClick = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const talentReport = useMemo(
    () =>
      isTalentReport && report
        ? normalizeTalentDiagnosticsAnalysisResponse({
            result: report.report as unknown as Record<string, unknown>,
          })
        : null,
    [isTalentReport, report]
  );

  // Prepare data for the circular chart dynamically from category scores
  const scoreData = websiteReport?.categoryScores
    ? websiteReport.categoryScores.map((cat: CategoryScore, i: number) => ({
        name: cat.category,
        value: cat.percentage,
        color: ["#4f46e5", "#db2777", "#10b981", "#ea580c"][i % 4],
        icon: [Sparkles, Megaphone, Gauge, Shield][i % 4],
      }))
    : [];

  const diagnosticConfig = {
    website: {
      title: "Website Diagnostics",
      summaryTitle: "Website Summary",
      description:
        "Analyze your website's performance, SEO, and health metrics.",

      reportTitle: "Website Diagnostic Report",
      urlLabel: "Analyzed Website",
      scoreLabel: "Website Health",
      opportunitiesTitle: "Website Improvement Opportunities",

      // CTA #1
      ctaTitle: "Ready to Take Your Website Further?",
      ctaDescription:
        "Whether you want a faster website, a stronger branding, or a smoother customer experience, we're here to help turn insights into real improvements.",
      ctaButtonText: "Contact Us",
      ctaBookingUrl: "https://app.strivreservices.com/strivre-services/strivreservices-form",

      // CTA #2
      reportCtaTitle: "Unlock the Full Website Health Report",
      reportCtaDescription: "Get the complete breakdown with detailed findings, improvement recommendations, and actionable insights.",
      reportCtaButtonText: "Buy Now",
      reportCtaBookingUrl: "https://app.strivreservices.com/strivre-services/strivreservices-form",

      // Keep object shape consistent with marketing/business
      helpTitle: "",
      helpIntro: "",
      helpServices: [],
      helpFooter: "",
      helpButtonText: "",
      helpButtonTextUrl: "",
    },

    marketing: {
      title: "Marketing Diagnostics",
      summaryTitle: "Marketing Summary",
      description:
        "Evaluate your marketing maturity, CRM processes, and growth opportunities.",

      reportTitle: "Marketing Diagnostic Report",
      urlLabel: "Marketing Assessment",
      scoreLabel: "Marketing Score",
      opportunitiesTitle: "Marketing Improvement Opportunities",

      ctaTitle: "Ready to Elevate Your Strategy?",
      ctaDescription:
        "Turn these diagnostic insights into a high-converting marketing engine. We specialize in building the digital foundations that drive growth.",
      ctaButtonText: "Contact Us",
      ctaBookingUrl: "https://app.strivreservices.com/strivre-services/strivreservices-form",

      helpTitle: "How We Can Help",
      helpIntro:
        "Based on your answers, here’s how we can support your marketing and CRM strategy:",

      helpServices: [
        "Marketing Strategy Development",
        "CRM Optimization",
        "Lead Generation Systems",
        "Marketing Automation",
        "Campaign Planning & Execution",
        "Sales & Marketing Alignment",
      ],

      helpFooter:
        "Partnering with us ensures your marketing efforts are clear, consistent, and effective — helping you grow your audience and achieve your business goals.",

      helpButtonText: "Contact Us",
      helpButtonTextUrl: "https://app.strivreservices.com/strivre-services/strivreservices-form",

      reportCtaTitle: "",
      reportCtaDescription: "",
      reportCtaButtonText: "",
      reportCtaBookingUrl: "",
    },

    business: {
      title: "Business Assessment",
      summaryTitle: "Business Summary",
      description:
        "Assess your operational health, business systems, and growth readiness.",

      reportTitle: "Business Assessment Report",
      urlLabel: "Business Assessment",
      scoreLabel: "Business Health",
      opportunitiesTitle: "Business Improvement Opportunities",

      ctaTitle: "Ready to Scale Your Business?",
      ctaDescription:
        "Turn these diagnostic insights into a roadmap for scalable growth. We specialize in optimizing the operational and digital structures of modern businesses.",
      ctaButtonText: "Schedule a Strategy Call",
      ctaBookingUrl: "https://app.strivreservices.com/strivre-services/strivreservices-form",

      helpTitle: "How We Can Help",
      helpIntro:
        "Based on your answers, here’s how we can support your business and digital strategy:",

      helpServices: [
        "Business Process Optimization",
        "Digital Transformation",
        "Technology Consulting",
        "Operational Efficiency Improvements",
        "Growth Strategy Development",
        "Business Systems Integration",
      ],

      helpFooter:
        "Partnering with us ensures your business remains modern, scalable, and ready for growth.",

      helpButtonText: "Schedule a Strategy Call",
      helpButtonTextUrl: "https://app.strivreservices.com/book/strivre-services",

      reportCtaTitle: "",
      reportCtaDescription: "",
      reportCtaButtonText: "",
      reportCtaBookingUrl: "",
    },

    talent: {
      title: "Talent Diagnostics",
      summaryTitle: "Talent Summary",
      description:
        "Evaluate your talent acquisition maturity, hiring performance, and workforce readiness.",

      reportTitle: "Talent Diagnostic Report",
      urlLabel: "Talent Acquisition Report",
      scoreLabel: "Talent Acquisition Health",
      opportunitiesTitle: "Talent Improvement Opportunities",

      ctaTitle: "Ready to Strengthen Talent Acquisition?",
      ctaDescription:
        "Turn these insights into an actionable plan for hiring, retention, and operational growth.",
      ctaButtonText: "Schedule a Talent Strategy Call",
      ctaBookingUrl: "https://app.strivreservices.com/book/strivre-services",

      helpTitle: "",
      helpIntro: "",
      helpServices: [],
      helpFooter: "",
      helpButtonText: "",
      helpButtonTextUrl: "",

      reportCtaTitle: "",
      reportCtaDescription: "",
      reportCtaButtonText: "",
      reportCtaBookingUrl: "",
    },
  } as const;

  const content =
    diagnosticConfig[
      diagnosticType as keyof typeof diagnosticConfig
    ] ?? diagnosticConfig.website;

  const pageWrapperClass = isAuthenticated
    ? "min-h-screen pt-10 p-6 md:p-8 lg:p-10 md:ml-0"
    : "min-h-screen px-4 sm:px-6 lg:px-8 text-slate-100";

  const generatedDate = report?.report?.createdDate
  ? new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(new Date(`${report.report.createdDate}Z`))
  : "N/A";

  return (
    <div className={pageWrapperClass}>
      {/* Header Section */}
      {!isAuthenticated ? (
          <header className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen bg-strivre-navy text-white border-b border-strivre-lightnavy shadow-lg mb-12">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
              
              {/* Logo */}
              <div className="flex items-center gap-3">
                <div className="bg-white rounded-xl px-2">
                  <img
                    src={strivreLogo}
                    alt="Strivre"
                    className="h-16 w-auto object-contain"
                    />
                </div>
              </div>

              {/* Navigation Links */}
              <nav className="hidden md:flex items-center gap-8">
                <a href="/diagnostics/website-tool"
                    className={
                      diagnosticType === "website"
                        ? "text-white font-semibold"
                        : "text-white/70 hover:text-white transition-colors"
                    }>
                  Web Diagnostics
                </a>
                <a href="/diagnostics/marketing-tool" 
                    className={
                      diagnosticType === "marketing"
                        ? "text-white font-semibold"
                        : "text-white/70 hover:text-white transition-colors"
                    }
                  >
                  Marketing Diagnostics
                </a>
                <a href="/diagnostics/business-assessment"
                    className={
                      diagnosticType === "business"
                        ? "text-white font-semibold"
                        : "text-white/70 hover:text-white transition-colors"
                    }>
                  Business Diagnostics
                </a>
                <a href="/diagnostics/talent-diagnostics"
                    className={
                      diagnosticType === "talent"
                        ? "text-white font-semibold"
                        : "text-white/70 hover:text-white transition-colors"
                    }>
                  Talent Diagnostics
                </a>
              </nav>

              <div className="flex items-center gap-3">
                <button
                  className="btn-primary btn-md shrink-0"
                  onClick={() => navigate("/login")}
                >
                  Login
                </button>

                {/* <button
                  className="btn-secondary btn-md shrink-0"
                  onClick={() => navigate("/register")}
                >
                  Sign Up
                </button> */}
              </div>
            </div>
          </header>
        ) : (
          <header className="mt-5 mb-8 flex items-center justify-between animate-fade-in">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2 leading-tight mb-0">
                <Search className="text-strivre-burgundy" size={24} />
                {content.title}
              </h1>
              <p className="text-slate-500 text-sm mt-1 text-left">
                {content.description}
              </p>
            </div>
          </header>
        )}

      {/* Loading State */}
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl text-center animate-fade-in">
            <div className="mb-4 flex justify-center">
              <div className="relative">
                <Spinner size="h-12 w-12" />
                <div className="absolute inset-0 animate-pulse"></div>
              </div>
            </div>
            <p className="text-slate-900 font-semibold mb-1">Loading Report</p>
            <p className="text-slate-600 text-sm">Please wait while we fetch your report...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="mb-6">
            <AlertCircle size={64} className="mx-auto text-red-500" />
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-2">Report Not Found</h2>
          <p className="text-slate-600 text-lg mb-8">
            {error?.message === "404" || error?.message?.includes("404")
              ? "The diagnostic report you're looking for doesn't exist or has expired."
              : "We encountered an error while loading the report. Please try again later."}
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-strivre-burgundy text-white font-semibold rounded-lg hover:bg-red-900 transition-colors"
          >
            <Home size={18} />
            Return to Home
          </a>
        </div>
      )}

      {/* Success State - Report Display */}
      {report && !isLoading && !isError && (
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8 animate-fade-in px-5">
          {isTalentReport && talentReport ? (
            <>
              <div>
                <h2 className="text-3xl font-bold tracking-tight text-slate-900">{content.reportTitle}</h2>
                <p className="text-lg font-semibold text-indigo-600">
                  {content.description}
                </p>
              </div>

              <TalentDiagnosticsSummary
                overallPercentage={talentReport.overallPercentage ?? 0}
                visualIndicator={talentReport.visualIndicator || "neutral"}
                benchmarkName={talentReport.benchmark?.name}
                benchmarkDescription={talentReport.benchmark?.description}
                benchmarkRecommendationContent={talentReport.benchmark?.recommendationContent}
                sectionResults={talentReport.sectionResults || []}
                strengths={talentReport.strengths || []}
                improvementOpportunities={talentReport.improvementOpportunities || []}
                recommendations={talentReport.recommendations || []}
                diagnosticStats={talentReport.scoringSnapshotJson ? JSON.parse(talentReport.scoringSnapshotJson) : {}}
                report={talentReport.summaryReport || []}
              />

              <div className="bg-slate-50 p-6 text-center">
                <p className="text-sm text-slate-600 mb-2">
                  Generated: <span className="font-semibold text-slate-900">
                    {generatedDate}
                  </span>
                </p>
                <p className="text-xs text-slate-500 mt-4">
                  This report was generated by <span className="font-semibold">Strivre Services</span>
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Website URL */}
              {diagnosticType === "website" && websiteReport?.url ? (
                <div>
                  <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                    {content.urlLabel}
                  </h2>

                  <a
                    href={websiteReport!.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-lg font-semibold text-indigo-600 hover:underline break-all"
                  >
                    {websiteReport!.url}
                  </a>
                </div>
              ) : (
                <div>
                  <h2 className="text-3xl font-bold tracking-tight text-slate-900">
                    {content.reportTitle}
                  </h2>

                  <p className="text-lg font-semibold text-indigo-600">
                    {content.description}
                  </p>
                </div>
              )}

              {/* 1. Top Section: Hero Bento Grid & Activity Rings */}
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Main Visualizer Card */}
                <div className="lg:col-span-4 bg-white rounded-3xl border border-strivre-lightnavy shadow-sm p-6 md:p-8 flex flex-col items-center justify-center text-center">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500"></div>

                  {/* Left Side: Avatar */}
                  <div className="flex-shrink-0">
                    <AvatarIndicator state={websiteReport!.visualIndicator} />
                  </div>

                  {/* Right Side: Score Result */}
                  <div className="flex flex-col items-center md:items-start">
                    <div className="bg-slate-50/50 backdrop-blur-sm px-8 py-6 rounded-[2.5rem] border border-slate-100 shadow-inner transition-all group-hover:bg-white group-hover:shadow-md">
                      <span className="text-7xl font-black text-slate-900 tracking-tighter leading-none block">
                        {websiteReport!.overallPercentage}
                        <span className="text-3xl text-indigo-600 font-bold ml-1">%</span>
                      </span>
                      <span className="text-[11px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-3 block">
                        {content.scoreLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metric Tiles */}
                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {scoreData.map((s: any) => (
                    <div key={s.name} className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 flex flex-col justify-between hover:border-blue-200 transition-all group">
                      <div className="flex justify-between items-start mb-4">
                        <div
                          className="p-3 rounded-2xl bg-slate-50 group-hover:bg-white transition-colors border border-transparent group-hover:border-slate-100"
                          style={{ color: s.color }}
                        >
                          <s.icon size={24} />
                        </div>
                        <span className="text-4xl font-black tracking-tighter" style={{ color: s.color }}>
                          {s.value}%
                        </span>
                      </div>
                      <div>
                        <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight mb-2">
                          {s.name}
                        </h4>
                        <div className="w-full h-1.5 bg-strivre-softgrey rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-1000"
                            style={{ width: `${s.value}%`, backgroundColor: s.color }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2. Key Improvement Opportunities */}
              {websiteReport!.recommendations.length > 0 && (
                <div className="bg-strivre-lightburgundy rounded-3xl border border-strivre-lightburgundy p-8 shadow-sm">
                  <h4 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                    <div className="p-2 bg-strivre-burgundy rounded-lg text-white">
                      <Lightbulb size={20} />
                    </div>
                    {content.opportunitiesTitle}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {websiteReport!.recommendations.map((rec: any, i: number) => {
                      const recommendationText =
                        typeof rec === "string" ? rec : rec.text;

                      return (
                        <div
                          key={i}
                          className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-indigo-50 shadow-sm"
                        >
                          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-strivre-burgundy text-white flex items-center justify-center font-bold text-sm">
                            {i + 1}
                          </span>

                          <p
                            className="text-slate-700 text-sm font-normal leading-relaxed text-left"
                            dangerouslySetInnerHTML={{ __html: recommendationText }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 3. Mid CTA Section */}
              <div className="relative bg-gradient-to-br from-blue-600 to-indigo-800 rounded-[2.5rem] p-12 text-white text-center shadow-2xl overflow-hidden group">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 pointer-events-none"></div>
                <div className="relative z-10">
                  <h3 className="text-3xl font-bold mb-4 tracking-tight text-white">{content.ctaTitle}</h3>
                  <p className="text-blue-100 text-lg leading-relaxed max-w-2xl mx-auto mb-8 font-normal">
                    {content.ctaDescription}
                  </p>
                  <button onClick={() => handleBookingClick(content.ctaBookingUrl)} className="px-10 py-4 bg-white text-blue-700 font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all active:scale-95">
                    {content.ctaButtonText}
                  </button>
                </div>
              </div>

              {/* 4. Report Summary Section */}
              {websiteReport!.summaryReport.length > 0 && (
                <div className="bg-white rounded-3xl border border-strivre-lightnavy p-10 shadow-sm relative">
                  <h3 className="text-2xl font-bold text-slate-900 mb-8 flex items-center gap-3">
                    <Megaphone size={22} className="text-indigo-600" />
                    {content.summaryTitle}
                  </h3>
                  <div className="space-y-6 text-left border-l-4 border-indigo-100 pl-8 ml-2">
                    {websiteReport!.summaryReport.map((para: string, i: number) => (
                      <p
                        key={i}
                        className="text-slate-600 leading-relaxed text-md font-normal"
                        dangerouslySetInnerHTML={{ __html: para }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 5. Final CTA Section */}
              {diagnosticType === "website" ? (
                <div hidden className="bg-strivre-navy rounded-[2.5rem] border-none shadow-2xl p-12 text-white text-center relative overflow-hidden">
                  <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-strivre-burgundy/20 rounded-full blur-[100px]"></div>
                  <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-80 h-80 bg-strivre-burgundy/10 rounded-full blur-[100px]"></div>

                  <div className="relative z-10">
                    <h3 className="text-3xl font-bold mb-4">
                      {content.reportCtaTitle}
                    </h3>

                    <p className="text-slate-400 leading-relaxed max-w-2xl mx-auto mb-10 text-lg">
                      {content.reportCtaDescription}
                    </p>

                    <button onClick={() => handleBookingClick(content.reportCtaBookingUrl)} className="px-12 py-5 bg-strivre-burgundy text-white font-bold rounded-full hover:bg-red-900 shadow-2xl shadow-red-600/20 transition-all transform hover:-translate-y-1">
                      {content.reportCtaButtonText}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-emerald-50/50 rounded-3xl border border-emerald-100 p-8 shadow-sm">
                  <h4 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Zap size={24} className="text-emerald-600" />
                    {content.helpTitle}
                  </h4>

                  <p className="text-slate-700 mb-6 text-base leading-relaxed text-left">
                    {content.helpIntro}
                  </p>

                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {content.helpServices.map((svc: string, i: number) => (
                      <li
                        key={i}
                        className="flex items-center gap-3 p-4 bg-white rounded-xl border border-emerald-50"
                      >
                        <Target
                          size={20}
                          className="text-emerald-600 flex-shrink-0"
                        />
                        <span className="text-slate-700 font-medium">
                          {svc}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <p className="text-slate-500 mt-6 text-sm text-left italic">
                    {content.helpFooter}
                  </p>

                  {content.helpButtonText && content.helpButtonTextUrl && (
                    <div className="mt-8 text-center">
                      <button
                        onClick={() =>
                          handleBookingClick(content.helpButtonTextUrl)
                        }
                        className="px-8 py-4 bg-emerald-600 text-white font-bold rounded-full hover:bg-emerald-700 transition-colors"
                      >
                        {content.helpButtonText}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* 4. Report Metadata */}
              <div className="bg-slate-50 p-6 text-center">
                <p className="text-sm text-slate-600 mb-2">
                  Generated: <span className="font-semibold text-slate-900">
                    {generatedDate}
                  </span>
                </p>
                <p className="text-xs text-slate-500 mt-4">
                  This report was generated by <span className="font-semibold">Strivre Services</span>
                </p>
              </div>
            </>
          )}
        </div>
      )}
      {!isAuthenticated && (
          <footer className="bottom-0 left-0 w-screen bg-strivre-navy border-t border-strivre-lightnavy py-4 z-40">
            <div className="max-w-7xl mx-auto px-6 text-center text-sm text-slate-500">
              © 2026 Strivre. All rights reserved.
            </div>
          </footer>
      )}
    </div>
  );
}
